//
// Copyright (C) 2024 - present Instructure, Inc.
//
// This file is part of Canvas.
//
// Canvas is free software: you can redistribute it and/or modify it under the
// terms of the GNU Affero General Public License as published by the Free
// Software Foundation, version 3 of the License.
//
// Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
// WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS
// FOR A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
// details.
//
// You should have received a copy of the GNU Affero General Public License
// along with this program. If not, see <http://www.gnu.org/licenses/>.

import React, {useEffect, useRef, useState} from 'react'
import $ from 'jquery'
import {CloseButton, IconButton} from '@instructure/ui-buttons'
import {DrawerLayout} from '@instructure/ui-drawer-layout'
import {Flex} from '@instructure/ui-flex'
import {Heading} from '@instructure/ui-heading'
import {IconLtiLine, IconFullScreenLine, IconExitFullScreenLine} from '@instructure/ui-icons'
import {Tooltip} from '@instructure/ui-tooltip'
import {Img} from '@instructure/ui-img'
import {TruncateText} from '@instructure/ui-truncate-text'
import {View} from '@instructure/ui-view'
import {handleExternalContentMessages} from '@canvas/external-tools/messages'
import ToolLaunchIframe from '@canvas/external-tools/react/components/ToolLaunchIframe'
import MutexManager from '@canvas/mutex-manager/MutexManager'
import type {Tool} from '@canvas/global/env/EnvCommon'
import iframeAllowances from '@canvas/external-apps/iframeAllowances'
import {onLtiClosePostMessage} from '@canvas/lti/jquery/messages'
import useBreakpoints from '@canvas/lti-apps/hooks/useBreakpoints'
import useGlobalNavWidth from './hooks/useGlobalNavWidth'

type Props = {
  tool: Tool | null
  pageContent: Element
  pageContentTitle: string
  pageContentMinWidth: string
  pageContentHeight: string
  trayPlacement: 'start' | 'end'
  onDismiss: any
  onResize: any
  onExternalContentReady?: any
  open: boolean
}

export default function ContentTypeExternalToolDrawer({
  tool,
  pageContent,
  pageContentTitle,
  pageContentMinWidth,
  pageContentHeight,
  trayPlacement,
  onDismiss,
  onResize,
  onExternalContentReady,
  open,
}: Props) {
  const queryParams = tool ? {display: 'borderless', placement: tool.placement} : {}
  const prefix = tool?.base_url.indexOf('?') === -1 ? '?' : '&'
  const iframeUrl = `${tool?.base_url}${prefix}${$.param(queryParams)}`
  const toolTitle = tool ? tool.title : 'External Tool'
  const toolIconUrl = tool?.icon_url
  const toolIconAlt = toolTitle ? `${toolTitle} Icon` : 'Tool Icon'
  const allow_fullscreen = tool?.allow_fullscreen
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const pageContentRef = useRef()
  // @ts-expect-error
  const initDrawerLayoutMutex = window.ENV.INIT_DRAWER_LAYOUT_MUTEX
  const STD_TRAY_WIDTH = window.ENV.FEATURES?.increased_top_nav_pane_size ? '33vw' : '320px'

  const {isMaxTablet} = useBreakpoints()
  const globalNavWidth = useGlobalNavWidth()

  const [isDrawerFullScreen, setIsDrawerFullScreen] = useState(false)
  const [trayWidth, setTrayWidth] = useState(STD_TRAY_WIDTH)

  useEffect(() => {
    if (isDrawerFullScreen) {
      setTrayWidth(`calc(100vw - ${globalNavWidth})`)
    } else if (isMaxTablet) {
      setTrayWidth('100vw')
    } else {
      setTrayWidth(STD_TRAY_WIDTH)
    }
  }, [isDrawerFullScreen, isMaxTablet, STD_TRAY_WIDTH, globalNavWidth])

  useEffect(() => {
    // If the drawer is closed from fullscreen, reset the drawer state.
    if (!open) {
      setIsDrawerFullScreen(false)
    }
  }, [open])

  useEffect(
    // setup DrawerLayout content
    () => {
      // appends pageContent to DrawerLayout.content
      if (pageContentRef.current && pageContent) {
        // @ts-expect-error
        pageContentRef.current.appendChild(pageContent)
      }
      /* Reparenting causes iFrames to reload or cancel load.
       * This ensures that any tool launch iFrames are not loaded
       * until after we complete reparenting.
       */
      if (initDrawerLayoutMutex) {
        MutexManager.releaseMutex(initDrawerLayoutMutex)
      }
    },
    [pageContent, initDrawerLayoutMutex],
  )

  useEffect(() => {
    window.addEventListener('resize', onResize)

    return () => {
      window.removeEventListener('resize', onResize)
    }
  }, [onResize])

  useEffect(
    // returns cleanup function:
    () => handleExternalContentMessages({ready: onExternalContentReady}),
    [onExternalContentReady],
  )

  useEffect(() => {
    // this drawer is mounted on page load, but listening for tool messages
    // only needs to happen when the drawer is open
    if (open) {
      return onLtiClosePostMessage(() => iframeRef.current, onDismiss)
    }
  }, [open, onDismiss])

  return (
    <View display="block" height={pageContentHeight}>
      <DrawerLayout minWidth={pageContentMinWidth}>
        <DrawerLayout.Content label={pageContentTitle} id="drawer-layout-content">
          {/* @ts-expect-error */}
          <div ref={pageContentRef} />
        </DrawerLayout.Content>
        <DrawerLayout.Tray
          label={toolTitle}
          open={open}
          placement={trayPlacement}
          onDismiss={onDismiss}
          data-testid="drawer-layout-tray"
          shouldCloseOnDocumentClick={false}
          themeOverride={{
            zIndex: 50,
          }}
        >
          <Flex height="100%" direction="column" padding="none none none none">
            <Flex.Item>
              <Flex
                height="1.5rem"
                justifyItems="space-between"
                alignItems="center"
                padding="medium small medium small"
                width={trayWidth}
                direction="row-reverse"
                data-testid="drawer-header"
              >
                <Flex.Item padding="none none none small">
                  <CloseButton
                    size="small"
                    onClick={onDismiss}
                    screenReaderLabel="Close"
                    data-testid="close-button"
                  />
                </Flex.Item>
                {allow_fullscreen && !isMaxTablet && (
                  <Flex.Item padding="none none none small">
                    <TrayExpansion
                      isDrawerFullScreen={isDrawerFullScreen}
                      toggleFullScreen={() => setIsDrawerFullScreen(prev => !prev)}
                    />
                  </Flex.Item>
                )}
                <Flex.Item shouldShrink={true} shouldGrow={true}>
                  <Heading level="h4" as="h2">
                    <TruncateText>{toolTitle}</TruncateText>
                  </Heading>
                </Flex.Item>
                <Flex.Item padding="none small none none">
                  {(toolIconUrl && <Img src={toolIconUrl} height="1rem" alt={toolIconAlt} />) || (
                    // @ts-expect-error
                    <IconLtiLine alt={toolIconAlt} />
                  )}
                </Flex.Item>
              </Flex>
            </Flex.Item>
            <Flex.Item shouldGrow={true}>
              {tool && (
                <ToolLaunchIframe
                  className={`tool_launch tool_launch_${tool.placement}`}
                  data-testid="ltiIframe"
                  ref={iframeRef}
                  src={iframeUrl}
                  title={toolTitle}
                  allow={iframeAllowances()}
                />
              )}
            </Flex.Item>
          </Flex>
        </DrawerLayout.Tray>
      </DrawerLayout>
    </View>
  )
}

type TrayExpansionProps = {
  isDrawerFullScreen: boolean
  toggleFullScreen: () => void
}

function TrayExpansion(props: TrayExpansionProps) {
  const {isDrawerFullScreen, toggleFullScreen} = props

  return (
    <Tooltip
      renderTip={isDrawerFullScreen ? 'Exit full screen' : 'Full screen'}
      on={['hover', 'focus']}
      placement="bottom"
    >
      <IconButton
        size="small"
        screenReaderLabel={isDrawerFullScreen ? 'Exit full screen' : 'Full screen'}
        onClick={toggleFullScreen}
        data-pendo="top-nav-tray-expansion"
        data-testid={isDrawerFullScreen ? 'exit-fullscreen-button' : 'fullscreen-button'}
      >
        {isDrawerFullScreen ? <IconExitFullScreenLine /> : <IconFullScreenLine />}
      </IconButton>
    </Tooltip>
  )
}
